import { Loader2 } from "lucide-react";
import { FC, FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSearchParams } from "react-router-dom";
import {
  usePasswordlessInitiate,
  usePasswordlessVerify,
} from "~/api/authentication/authentication";
import bagIcon from "~/assets/bag.svg";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { storeAccessToken, storeRefreshToken, storeUsername } from "~/helpers";
import { trackEvent } from "~/utils/analytics";

interface GuestLoginFormProps {
  email: string;
  caseName?: string;
  shareToken?: string;
  onSuccess: () => void;
}

const RESEND_COOLDOWN = 60; // seconds
const SESSION_STORAGE_KEY = "guest_login_session";

const maskEmail = (email: string): string => {
  const [localPart, domain] = email.split("@");
  if (!domain) return email;
  const maskedLocal = localPart.slice(0, 3) + "****";
  return `${maskedLocal}@${domain}`;
};

export const GuestLoginForm: FC<GuestLoginFormProps> = ({
  email,
  caseName,
  shareToken,
  onSuccess,
}) => {
  const intl = useIntl();
  const [searchParams] = useSearchParams();

  // Check for code and session from URL
  const codeFromUrl = searchParams.get("code");
  const sessionFromStorage = localStorage.getItem(SESSION_STORAGE_KEY);
  const hasCodeInUrl = !!(codeFromUrl && /^\d{6}$/.test(codeFromUrl));
  const hasUrlCredentials = hasCodeInUrl && !!sessionFromStorage;

  const [session, setSession] = useState<string>(
    hasUrlCredentials ? sessionFromStorage : ""
  );
  const [otpDigits, setOtpDigits] = useState<string[]>(() => {
    if (hasCodeInUrl) {
      return codeFromUrl.split("");
    }
    return ["", "", "", "", "", ""];
  });
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [hasSentInitialCode, setHasSentInitialCode] = useState(hasCodeInUrl);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const isSubmittingRef = useRef(false);

  const { mutate: initiate, isPending: isInitiating } =
    usePasswordlessInitiate();
  const { mutate: verify, isPending: isVerifying } = usePasswordlessVerify();

  const handleSendCode = useCallback(() => {
    setError(null);
    trackEvent({ eventName: "app/share/guest_login/send_code" });

    initiate(
      {
        data: {
          email,
          caseName,
          shareToken,
        },
      },
      {
        onSuccess: (data) => {
          if (data.session) {
            setSession(data.session);
            localStorage.setItem(SESSION_STORAGE_KEY, data.session);
            setResendCooldown(RESEND_COOLDOWN);
          }
        },
        onError: () => {
          setError(
            intl.formatMessage({
              id: "share.login.error.sendCode",
              defaultMessage:
                "Failed to send verification code. Please try again.",
            })
          );
        },
      }
    );
  }, [email, initiate, intl]);

  // Auto-send code on mount
  useEffect(() => {
    if (!hasSentInitialCode && email) {
      handleSendCode();
      setHasSentInitialCode(true);
    }
  }, [email, hasSentInitialCode, handleSendCode]);

  const handleDigitChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newDigits = [...otpDigits];

    // Handle paste of full code
    if (value.length > 1) {
      const digits = value.slice(0, 6).split("");
      digits.forEach((digit, i) => {
        if (i < 6) newDigits[i] = digit;
      });
      setOtpDigits(newDigits);
      const lastFilledIndex = Math.min(digits.length - 1, 5);
      inputRefs.current[lastFilledIndex]?.focus();
      return;
    }

    newDigits[index] = value;
    setOtpDigits(newDigits);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyCode = useCallback(
    (e?: FormEvent) => {
      e?.preventDefault();

      // Prevent duplicate submissions
      if (isSubmittingRef.current) return;

      setError(null);

      const code = otpDigits.join("");
      if (code.length !== 6) {
        setError(
          intl.formatMessage({
            id: "share.login.error.invalidCode",
            defaultMessage: "Please enter a valid 6-digit code",
          })
        );
        return;
      }

      isSubmittingRef.current = true;
      trackEvent({ eventName: "app/share/guest_login/verify_code" });

      verify(
        {
          data: {
            email,
            code,
            session,
          },
        },
        {
          onSuccess: (data) => {
            localStorage.removeItem(SESSION_STORAGE_KEY);
            storeAccessToken(data.accessToken);
            storeRefreshToken(data.refreshToken);
            storeUsername(data.username);
            trackEvent({ eventName: "app/share/guest_login/success" });
            onSuccess();
          },
          onError: () => {
            isSubmittingRef.current = false;
            setError(
              intl.formatMessage({
                id: "share.login.error.verifyCode",
                defaultMessage: "Invalid or expired code. Please try again.",
              })
            );
          },
        }
      );
    },
    [email, intl, onSuccess, otpDigits, session, verify]
  );

  // Auto-submit when all digits are entered
  useEffect(() => {
    const code = otpDigits.join("");
    if (code.length === 6 && session) {
      handleVerifyCode();
    }
  }, [otpDigits, session, handleVerifyCode]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(
        () => setResendCooldown(resendCooldown - 1),
        1000
      );
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleResendCode = () => {
    if (resendCooldown > 0) return;
    setOtpDigits(["", "", "", "", "", ""]);
    handleSendCode();
  };

  return (
    <div className="flex flex-col gap-6 max-w-[360px]">
      <div className="flex w-[32px]">
        <img src={bagIcon} alt="Bag Icon" className="text-gray-400 mb-[16px]" />
      </div>

      <div>
        <h1 className="text-[24px] font-semibold text-gray-900 dark:text-white">
          <FormattedMessage
            id="share.login.title"
            defaultMessage="Check Your Email"
          />
        </h1>
        <p className="mt-[16px] text-sm text-[#71717A] dark:text-gray-400">
          <FormattedMessage
            id="share.login.subtitle"
            defaultMessage="Code sent to {email}. Enter it below to access the {caseName} case."
            values={{
              email: <strong>{maskEmail(email)}</strong>,
              caseName: caseName || "shared",
            }}
          />
        </p>
      </div>

      <form onSubmit={handleVerifyCode} className="flex flex-col gap-8">
        <div className="flex flex-col gap-[8px]">
          <Label>
            <FormattedMessage
              id="share.login.codeLabel"
              defaultMessage="Enter code"
            />
          </Label>
          <div className="flex">
            {otpDigits.map((digit, index) => (
              <Input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={digit}
                onChange={(e) => handleDigitChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className={`w-[60px] h-[40px] text-center text-lg font-medium border-r-0 ${error ? "border-red-500" : "border-[#E4E4E7]"} ${index === 0 ? "rounded-l-md rounded-r-none" : "rounded-none"} ${index === 5 ? "rounded-r-md border-l-0 border" : ""}`}
                autoFocus={index === 0}
                disabled={isVerifying}
              />
            ))}
          </div>
          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>

        {isVerifying && (
          <div className="flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        )}

        <div className="flex justify-center">
          <button
            type="button"
            onClick={handleResendCode}
            disabled={resendCooldown > 0 || isInitiating}
            className="text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed dark:text-blue-400 dark:hover:text-blue-500"
          >
            {resendCooldown > 0 ? (
              <FormattedMessage
                id="share.login.resendCodeCooldown"
                defaultMessage="Resend Code ({seconds} sec)"
                values={{ seconds: resendCooldown }}
              />
            ) : (
              <FormattedMessage
                id="share.login.resendCode"
                defaultMessage="Resend Code"
              />
            )}
          </button>
        </div>
      </form>
    </div>
  );
};
